import { useEffect, useState } from "react";
import { api, errText, photoUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { PROVINCES } from "../../lib/constants";
import { SELLER_TYPES, timeAgo } from "./adminShared";
import { HolidayEmail } from "./HolidayEmail";
import { Megaphone, FileText, X, ImagePlus, Loader2, Send, Clock, BarChart3, Users, MousePointerClick, TrendingUp, Trophy, ExternalLink } from "lucide-react";

export function Announce() {
  const [message, setMessage] = useState("");
  const [alsoEmail, setAlsoEmail] = useState(false);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [province, setProvince] = useState("all");
  const [sellerType, setSellerType] = useState("all");
  const [images, setImages] = useState([]);
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const loadAnalytics = () => api.get("/admin/broadcast-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  const load = () => {
    api.get("/admin/broadcasts").then((r) => setHistory(r.data)).catch((e) => toast.error(errText(e)));
    loadAnalytics();
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MAX_IMAGES = 6;
  const onImage = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) return toast.error(`Up to ${MAX_IMAGES} images per announcement`);
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) toast.error(`Only ${remaining} more image${remaining === 1 ? "" : "s"} allowed`);
    setUploadingImg(true);
    try {
      for (const file of toUpload) {
        if (!file.type.startsWith("image/")) { toast.error("Please choose image files only"); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.path]);
      }
    } catch (err) { toast.error(errText(err)); }
    finally { setUploadingImg(false); }
  };

  const send = async () => {
    if (!!ctaLabel.trim() !== !!ctaUrl.trim()) {
      return toast.error("Your button needs both a label and a link");
    }
    setSending(true);
    try {
      const payload = { message: message.trim(), email: alsoEmail };
      if (scheduleOn && scheduleAt) payload.scheduled_at = new Date(scheduleAt).toISOString();
      if (province !== "all") payload.province = province;
      if (sellerType !== "all") payload.seller_type = sellerType;
      if (images.length) payload.images = images;
      if (ctaLabel.trim() && ctaUrl.trim()) { payload.cta_label = ctaLabel.trim(); payload.cta_url = ctaUrl.trim(); }
      const { data } = await api.post("/admin/broadcast", payload);
      if (data.status === "scheduled") {
        toast.success(`Announcement scheduled for ${new Date(data.scheduled_at).toLocaleString()}`);
      } else {
        toast.success(`Sending to ${data.sent} inbox${data.sent === 1 ? "" : "es"}${alsoEmail ? " + email" : ""}…`);
      }
      setMessage(""); setScheduleAt(""); setScheduleOn(false); setProvince("all"); setSellerType("all");
      setImages([]); setCtaLabel(""); setCtaUrl(""); setConfirm(false);
      load();
      setTimeout(load, 2500); // refresh once delivery finishes in the background
    } catch (e) { toast.error(errText(e)); }
    finally { setSending(false); }
  };

  const cancelScheduled = async (id) => {
    try { await api.delete(`/admin/broadcasts/${id}`); toast.success("Scheduled announcement cancelled"); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const [templates, setTemplates] = useState([]);
  const loadTemplates = () => api.get("/admin/broadcast-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (t) => {
    setMessage(t.message || "");
    setImages(t.images || []);
    setCtaLabel(t.cta_label || "");
    setCtaUrl(t.cta_url || "");
    toast.success(`Loaded "${t.name}"`);
  };

  const saveTemplate = async () => {
    const name = window.prompt("Name this template (e.g. Adoption weekend promo):");
    if (name === null) return;
    if (!name.trim()) return toast.error("Template name required");
    try {
      await api.post("/admin/broadcast-templates", {
        name: name.trim(), message: message.trim(), images,
        cta_label: ctaLabel.trim(), cta_url: ctaUrl.trim(),
      });
      toast.success("Template saved");
      loadTemplates();
    } catch (e) { toast.error(errText(e)); }
  };

  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/admin/broadcast-templates/${id}`); loadTemplates(); }
    catch (err) { toast.error(errText(err)); }
  };

  const statusBadge = (b) => {
    const s = b.status || "sent";
    const map = {
      scheduled: { label: "Scheduled", cls: "bg-violet-100 text-violet-700" },
      processing: { label: "Sending…", cls: "bg-amber-100 text-amber-700" },
      sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-700" },
    };
    const m = map[s] || map.sent;
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
  };

  const minDt = new Date(Date.now() + 60000).toISOString().slice(0, 16);

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <HolidayEmail onSent={load} />
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="announce-compose">
        <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-1"><Megaphone size={18} className="text-[#F5551E]" /> Broadcast to all users</h3>
        <p className="text-sm text-slate-500 mb-4">Send a one-way message that lands in every regular user's inbox from the CritterPosts Team.</p>
        {templates.length > 0 && (
          <div className="mb-4" data-testid="announce-templates">
            <p className="text-xs font-semibold text-slate-500 mb-1.5">Templates</p>
            <div className="flex flex-wrap gap-2">
              {templates.map((t) => (
                <button key={t.id} onClick={() => applyTemplate(t)} data-testid={`template-${t.id}`}
                  className="group inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-[#F5551E] hover:text-[#F5551E] transition-colors">
                  <FileText size={13} /> {t.name}
                  <span onClick={(e) => deleteTemplate(t.id, e)} data-testid={`template-delete-${t.id}`} className="ml-0.5 opacity-40 group-hover:opacity-100 hover:text-red-500"><X size={12} /></span>
                </button>
              ))}
            </div>
          </div>
        )}
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="announce-message"
          rows={6}
          maxLength={1000}
          placeholder="e.g. Hi {{name}}, we're running a free adoption weekend in {{province}} — post your rescue pets now! 🐾"
          className="rounded-xl resize-none"
        />
        <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="announce-placeholders">
          <span className="text-xs text-slate-400">Personalize:</span>
          {[["{{name}}", "First name"], ["{{province}}", "Province"]].map(([token, label]) => (
            <button
              key={token}
              type="button"
              onClick={() => setMessage((m) => `${m}${m && !m.endsWith(" ") ? " " : ""}${token} `)}
              data-testid={`insert-${token.replace(/[^a-z]/gi, "")}`}
              className="text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-[#F5551E] hover:text-[#F5551E] transition-colors"
            >
              + {label}
            </button>
          ))}
          <span className="text-[11px] text-slate-400">auto-filled per recipient</span>
        </div>
        <div className="mt-3" data-testid="announce-image-section">
          <div className="flex flex-wrap gap-2">
            {images.map((im, ix) => (
              <div key={ix} className="relative">
                <img src={photoUrl(im)} alt="attachment" data-testid={`announce-image-preview-${ix}`} className="h-20 w-20 rounded-xl border border-slate-200 object-cover" />
                <button type="button" onClick={() => setImages((p) => p.filter((_, i) => i !== ix))} data-testid={`announce-image-remove-${ix}`} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"><X size={14} /></button>
              </div>
            ))}
            {images.length < 6 && (
              <label data-testid="announce-image-label" className="h-20 w-20 flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 text-slate-400 hover:border-[#F5551E] hover:text-[#F5551E] cursor-pointer transition-colors">
                {uploadingImg ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                <span className="text-[10px] font-medium">Add image</span>
                <input type="file" accept="image/*" multiple hidden onChange={onImage} data-testid="announce-image-input" />
              </label>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Attach up to 6 images (optional)</p>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="announce-cta-section">
          <div>
            <span className="text-xs text-slate-500">Button label (optional)</span>
            <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} data-testid="announce-cta-label" maxLength={40} placeholder="e.g. See adoption weekend" className="rounded-xl mt-1" />
          </div>
          <div>
            <span className="text-xs text-slate-500">Button link</span>
            <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} data-testid="announce-cta-url" placeholder="https://…  or  /browse" className="rounded-xl mt-1" />
          </div>
        </div>
        {(!!ctaLabel.trim() !== !!ctaUrl.trim()) && (
          <p className="text-xs text-red-500 mt-1" data-testid="announce-cta-hint">Add both a label and a link to include a button.</p>
        )}
        <label className="flex items-center gap-2 mt-3 text-sm text-slate-600 cursor-pointer">
          <Checkbox checked={alsoEmail} onCheckedChange={(v) => setAlsoEmail(!!v)} data-testid="announce-email-toggle" />
          Also email every user so it reaches them offline
        </label>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div>
            <span className="text-xs text-slate-500">Province</span>
            <Select value={province} onValueChange={setProvince}>
              <SelectTrigger className="rounded-xl bg-white mt-1" data-testid="announce-province"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">All provinces</SelectItem>
                {PROVINCES.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <span className="text-xs text-slate-500">Seller type</span>
            <Select value={sellerType} onValueChange={setSellerType}>
              <SelectTrigger className="rounded-xl bg-white mt-1" data-testid="announce-seller-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All seller types</SelectItem>
                {SELLER_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          {province === "all" && sellerType === "all"
            ? "Sending to every user."
            : `Targeting: ${[province !== "all" ? PROVINCES.find((p) => p.code === province)?.name : null, sellerType !== "all" ? `${sellerType} sellers` : null].filter(Boolean).join(" · ")}${province !== "all" ? " (users with a listing there)" : ""}`}
        </p>
        <label className="flex items-center gap-2 mt-2 text-sm text-slate-600 cursor-pointer">
          <Checkbox checked={scheduleOn} onCheckedChange={(v) => setScheduleOn(!!v)} data-testid="announce-schedule-toggle" />
          Schedule for later
        </label>
        {scheduleOn && (
          <input
            type="datetime-local"
            value={scheduleAt}
            min={minDt}
            onChange={(e) => setScheduleAt(e.target.value)}
            data-testid="announce-schedule-at"
            className="mt-2 w-full h-11 px-3 rounded-xl border border-slate-200 outline-none focus:border-[#F5551E] text-sm"
          />
        )}
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-slate-400">{message.length}/1000</span>
          <div className="flex items-center gap-2">
            <Button onClick={saveTemplate} disabled={!message.trim() && !images.length} variant="outline" data-testid="announce-save-template" className="rounded-xl gap-1.5"><FileText size={15} /> Save template</Button>
            <Button onClick={() => setConfirm(true)} disabled={!message.trim() || sending || (scheduleOn && !scheduleAt) || (!!ctaLabel.trim() !== !!ctaUrl.trim())} data-testid="announce-send" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2 disabled:opacity-40">
              {sending ? <Loader2 size={16} className="animate-spin" /> : scheduleOn ? <Clock size={16} /> : <Send size={16} />} {scheduleOn ? "Schedule" : "Send announcement"}
            </Button>
          </div>
        </div>
      </div>

      {analytics && analytics.campaigns && analytics.campaigns.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="campaign-dashboard">
          <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-1"><BarChart3 size={18} className="text-[#F5551E]" /> Campaign dashboard</h3>
          <p className="text-xs text-slate-400 mb-4">Compare reach &amp; click-through rate across sent announcements</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid="camp-stat-count">
              <p className="text-[11px] text-slate-400 font-medium">Campaigns</p>
              <p className="text-xl font-display font-bold text-[#12234A]">{analytics.totals.count}</p>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid="camp-stat-reach">
              <p className="text-[11px] text-slate-400 font-medium flex items-center gap-1"><Users size={12} /> Total reach</p>
              <p className="text-xl font-display font-bold text-[#12234A]">{analytics.totals.total_reach.toLocaleString()}</p>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid="camp-stat-clicks">
              <p className="text-[11px] text-slate-400 font-medium flex items-center gap-1"><MousePointerClick size={12} /> Total clicks</p>
              <p className="text-xl font-display font-bold text-[#12234A]">{analytics.totals.total_clicks.toLocaleString()}</p>
            </div>
            <div className="rounded-xl bg-[#FFF4F0] border border-[#F5551E]/20 p-3" data-testid="camp-stat-ctr">
              <p className="text-[11px] text-[#F5551E] font-medium flex items-center gap-1"><TrendingUp size={12} /> Avg CTR</p>
              <p className="text-xl font-display font-bold text-[#F5551E]">{analytics.totals.avg_ctr != null ? `${analytics.totals.avg_ctr}%` : "—"}</p>
            </div>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="campaign-list">
            {analytics.campaigns.map((c) => {
              const isBest = analytics.totals.best_id && c.id === analytics.totals.best_id && c.ctr != null;
              return (
                <div key={c.id} className={`rounded-xl border p-3 ${isBest ? "border-[#F5551E]/40 bg-[#FFF9F6]" : "border-slate-100"}`} data-testid={`campaign-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#12234A] truncate">{c.message}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {timeAgo(c.sent_at || c.created_at)}
                        {c.target_label && c.target_label !== "All users" ? ` · 🎯 ${c.target_label}` : ""}
                        {isBest && <span className="inline-flex items-center gap-0.5 ml-1 text-[#F5551E] font-semibold"><Trophy size={11} /> Top performer</span>}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-[#12234A]" data-testid={`campaign-ctr-${c.id}`}>{c.ctr != null ? `${c.ctr}%` : "—"}</p>
                      <p className="text-[10px] text-slate-400">CTR</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1" data-testid={`campaign-reach-${c.id}`}><Users size={12} className="text-slate-400" /> {c.reach.toLocaleString()} reached</span>
                    {c.has_cta ? (
                      <span className="inline-flex items-center gap-1" data-testid={`campaign-clicks-${c.id}`}><MousePointerClick size={12} className="text-slate-400" /> {c.clicks.toLocaleString()} click{c.clicks === 1 ? "" : "s"}</span>
                    ) : (
                      <span className="text-slate-300 italic">No button</span>
                    )}
                    {c.emailed > 0 && <span className="inline-flex items-center gap-1">✉ {c.emailed.toLocaleString()} emailed</span>}
                  </div>
                  {c.has_cta && (
                    <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#F5551E]" style={{ width: `${Math.min(c.ctr || 0, 100)}%` }} data-testid={`campaign-bar-${c.id}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="announce-history">
        <h3 className="font-display font-bold text-[#12234A] mb-4">Recent announcements</h3>
        {!history ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No announcements sent yet.</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {history.map((b) => (
              <div key={b.id} className="border border-slate-100 rounded-xl p-3" data-testid={`broadcast-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 flex items-start gap-2">
                    {(() => { const imgs = (b.images && b.images.length) ? b.images : (b.image ? [b.image] : []); return imgs.length > 0 && (
                      <div className="flex -space-x-2 shrink-0">
                        {imgs.slice(0, 3).map((im, ix) => <img key={ix} src={photoUrl(im)} alt="" data-testid={`broadcast-image-${b.id}-${ix}`} className="h-10 w-10 rounded-lg object-cover bg-slate-100 border-2 border-white" />)}
                        {imgs.length > 3 && <span className="h-10 w-10 rounded-lg bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-slate-500">+{imgs.length - 3}</span>}
                      </div>
                    ); })()}
                    <div className="flex-1">
                      {b.kind === "seasonal" && b.subject && <p className="text-sm font-semibold text-[#12234A]">{b.subject}</p>}
                      <p className="text-sm text-[#12234A]">{b.message}</p>
                      {b.cta_label && b.cta_url && <span data-testid={`broadcast-cta-${b.id}`} className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-[#F5551E] bg-[#FFF4F0] rounded-full px-2 py-0.5"><ExternalLink size={11} /> {b.cta_label} · <span data-testid={`broadcast-cta-clicks-${b.id}`}>{b.cta_clicks || 0} click{(b.cta_clicks || 0) === 1 ? "" : "s"}</span></span>}
                    </div>
                  </div>
                  {statusBadge(b)}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-400">
                    {b.status === "scheduled"
                      ? `Scheduled for ${new Date(b.scheduled_at).toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" })} Toronto`
                      : `${b.sender_name} · ${timeAgo(b.created_at)} · ${b.recipients} recipients${b.emailed ? ` · ${b.emailed} emailed` : ""}`}
                    {b.target_label && b.target_label !== "All users" ? ` · 🎯 ${b.target_label}` : ""}
                  </p>
                  {b.status === "scheduled" && (
                    <button onClick={() => cancelScheduled(b.id)} data-testid={`cancel-broadcast-${b.id}`} className="text-xs text-red-500 hover:underline font-medium">Cancel</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-white" data-testid="announce-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{scheduleOn ? "Schedule this announcement?" : "Send to all users?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {scheduleOn
                ? `It will be delivered to every user's inbox${alsoEmail ? " and emailed" : ""} at the scheduled time.`
                : `This message will be delivered to every regular user's inbox${alsoEmail ? " and emailed to them" : ""}. This can't be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={send} data-testid="announce-confirm-send" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812]">{scheduleOn ? "Schedule" : "Send now"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
